import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { assign, enqueueActions, fromPromise, raise, sendParent, setup } from "xstate";

import type { GitAheadBehind, GitMergeAnalysis } from "~/data/git";
import { tauriApi } from "~/state/api/tauri";
import {
	authenticationActorRef,
	waitForAccessTokenSilently,
} from "~/state/machines/authentication";
import type {
	BranchInfo,
	BranchInfo as GitBranchInfo,
} from "~/state/machines/git/gitBranches.machine";

import { GIT_PUBLISHING_BRANCH_NAME, GIT_PUBLISHING_REMOTE_NAME } from "../addGitMomentRemote";
import type {
	GitBranchAheadBehindEvent,
	GitBranchAnalyzeEvent,
	GitBranchCreateEvent,
	GitBranchDeleteEvent,
	GitBranchMergeEvent,
	GitBranchSwitchEvent,
	GitBranchTrackEvent,
	GitCommitEvent,
	GitPushEvent,
	GitRemote,
	GitRemoteAddEvent,
	GitRepoHistory,
	GitRepoStatus,
	GitRepositoryMachineContext,
	GitRepositoryMachineEmitted,
	GitRepositoryMachineEvent,
	GitRepositoryMachineFullEvent,
	GitRepositoryMachineInternalEvent,
	GitShow,
	GitShowEvent,
	GitStageEvent,
} from "./git-repository.types";

/**
 * # Git Repository Machine
 *
 * This machine is responsible for managing the git repository for a given document. All git operations are handled by this machine.
 */
export const gitRepositoryMachine = setup({
	types: {
		context: {} as GitRepositoryMachineContext,
		input: {} as {
			path: string;
		},
		events: {} as
			| GitRepositoryMachineEvent
			| GitRepositoryMachineInternalEvent
			| GitRepositoryMachineEmitted,
		emitted: {} as GitRepositoryMachineEmitted,
	},
	actors: {
		gitCommit: fromPromise<void, Parameters<typeof tauriApi.commit>[0]>(({ input }) => {
			return tauriApi.commit(input);
		}),
		gitDiff: fromPromise<void, { path: string }>(async ({ input }) => {
			return tauriApi.diff(input);
		}),
		gitFetch: fromPromise<void, { path: string; remote: string; branch: string }>(
			async ({ input }) => {
				const token = await waitForAccessTokenSilently(authenticationActorRef);
				return tauriApi.fetch({ ...input, token });
			}
		),
		gitInit: fromPromise<void, { path: string }>(async ({ input }) => {
			return tauriApi.init(input);
		}),
		gitPull: fromPromise<void, { path: string }>(async ({ input }) => {
			const token = await waitForAccessTokenSilently(authenticationActorRef);
			return tauriApi.pull({ ...input, token });
		}),
		gitPush: fromPromise<void, { path: string; remote: string; branch: string }>(
			async ({ input }) => {
				const token = await waitForAccessTokenSilently(authenticationActorRef);
				return tauriApi.push({ ...input, token });
			}
		),
		gitRemoteAdd: fromPromise<void, { path: string; remote: string; url: string }>(
			async ({ input }) => {
				return tauriApi.remoteAdd(input);
			}
		),
		gitRemoteList: fromPromise<GitRemote[], { path: string }>(async ({ input }) => {
			return tauriApi.remoteList(input) || [];
		}),
		gitLog: fromPromise<GitRepoHistory, { path: string }>(async ({ input }) => {
			return tauriApi.repoHistory(input);
		}),
		gitShow: fromPromise<GitShow, { path: string; commitHash: string }>(async ({ input }) => {
			const rawShow = await tauriApi.show(input);

			return {
				header: {
					prettyCommitSha: rawShow.header.commit_sha,
					authorName: rawShow.header.author.name,
					authorEmail: rawShow.header.author.email,
					prettyDate: rawShow.header.commit_time,
					timeSinceCommit: formatDistanceToNowStrict(
						parseISO(rawShow.header.commit_time)
					),
					commitMessage: rawShow.header.commit_message,
				},
				originalDiff: rawShow.original_diff,
				contents: rawShow.contents,
				parsedFiles: rawShow.parsed_files,
			};
		}),
		gitStage: fromPromise<void, Parameters<typeof tauriApi.stage>[0]>(async ({ input }) => {
			return tauriApi.stage(input);
		}),
		gitBranchNew: fromPromise<void, { path: string; name: string }>(async ({ input }) => {
			return tauriApi.branchNew(input);
		}),
		gitBranchList: fromPromise<GitBranchInfo[], { path: string }>(async ({ input }) => {
			return tauriApi.branchList(input) || [];
		}),
		gitBranchTrack: fromPromise<
			void,
			{ path: string; remote: string; localBranch: string; upstreamBranch: string }
		>(async ({ input }) => {
			const token = await waitForAccessTokenSilently(authenticationActorRef);
			return tauriApi.branchTrack({ ...input, token });
		}),
		gitBranchSwitch: fromPromise<BranchInfo, { path: string; target: string }>(
			async ({ input }) => {
				return tauriApi.branchSwitch(input);
			}
		),
		gitBranchMerge: fromPromise<
			void,
			{ path: string; source: string; target: string; deleteSource: boolean }
		>(async ({ input }) => {
			return tauriApi.branchMerge(input);
		}),
		gitBranchAnalyze: fromPromise<
			GitMergeAnalysis,
			{ path: string; source: string; target: string }
		>(async ({ input }) => {
			return tauriApi.branchAnalyze(input);
		}),
		gitBranchDelete: fromPromise<void, { path: string; name: string }>(async ({ input }) => {
			return tauriApi.branchDelete(input);
		}),
		gitBranchAheadBehind: fromPromise<
			GitAheadBehind,
			{ path: string; source: string; target: string }
		>(async ({ input }) => {
			return tauriApi.branchAheadBehind(input);
		}),
		gitStatus: fromPromise<GitRepoStatus | undefined, { path: string }>(async ({ input }) => {
			const rawStatus = await tauriApi.status({ path: input.path });

			if (!rawStatus.is_repo) {
				return undefined;
			}

			return {
				isClean: rawStatus.is_clean,
				ahead: rawStatus.ahead,
				behind: rawStatus.behind,
				branch: rawStatus.branch,
				commit: rawStatus.commit,
				staged: rawStatus.staged,
				files: rawStatus.files.map((file) => ({
					from: file.from,
					path: file.path || "",
					index: file.index || "",
					working_dir: file.working_dir || "",
				})),
				changed: rawStatus.changed,
				insertions: rawStatus.insertions,
				deletions: rawStatus.deletions,
			};
		}),
	},
	guards: {
		hasCommandInQueue: ({ context }) => context.commandQueue.length > 0,
		eventIsDone: ({ event }) => event.type.endsWith(".done") || event.type.endsWith(".error"),
		eventIsRequest: ({ event }) =>
			!event.type.endsWith(".done") &&
			!event.type.endsWith(".error") &&
			!event.type.endsWith(".processing"),
	},
	actions: {
		addToQueue: assign(({ context, event }) => ({
			commandQueue: [...context.commandQueue, event as GitRepositoryMachineFullEvent],
		})),
		updateDiff: raise({
			type: "git.diff",
		}),
		updateStatus: raise({
			type: "git.status",
		}),
		updateBranches: raise({
			type: "git.branch.list",
		}),
		processingEventDone: enqueueActions(({ context, enqueue, event }) => {
			const processingEvent = context.commandQueue.shift();
			if (!processingEvent) {
				return;
			}

			const emitting: GitRepositoryMachineEmitted = {
				type: `${processingEvent.type}.done`,
				payload: processingEvent.payload,
				output: (event as GitRepositoryMachineEmitted).output,
				error: undefined,
			};

			enqueue.raise(emitting);

			// If the event has a sender attached, send them an event
			if (processingEvent.sender) {
				enqueue.sendTo(processingEvent.sender, emitting);
			}

			enqueue.emit(emitting);
		}),
		processingEventError: enqueueActions(({ context, event, enqueue }) => {
			console.log(`processingEventError ${JSON.stringify(event)}`);

			const processingEvent = context.commandQueue.shift();
			if (!processingEvent) {
				return;
			}

			let errorEmit: undefined | object = undefined;
			if ("error" in event) {
				errorEmit = event.error;
			}

			const emitting: GitRepositoryMachineEmitted = {
				type: `${processingEvent.type}.error`,
				payload: processingEvent.payload,
				output: undefined,
				error: { msg: errorEmit },
			};

			enqueue.raise(emitting);

			enqueue.emit(emitting);

			// If the event has a sender attached, send them an event
			if (processingEvent.sender) {
				enqueue.sendTo(processingEvent.sender, emitting);
			}
		}),
	},
}).createMachine({
	id: "gitRepository",
	context: ({ input }) => ({
		path: input.path,
		commandQueue: [],
		status: undefined,
		history: undefined,
		branches: [],
		remotes: [],
	}),

	// When the machine is created, we want to get the status of the repository before doing anything else
	entry: raise({
		type: "git.status",
	}),

	type: "parallel",
	states: {
		queue: {
			description: "Queue of commands that have been sent to the machine",
			initial: "idle",
			states: {
				idle: {
					description:
						"Not currently processing any commands and will automatically move to processing when one is added to the queue",
					always: {
						guard: "hasCommandInQueue",
						target: "#gitRepository.queue.processing",
					},
				},
				processing: {
					description: "Currently processing a command",
					entry: raise(({ context }) => ({
						type: `${context.commandQueue[0]!.type}.processing`,
						payload: context.commandQueue[0]!.payload,
					})),
					on: {
						"git.*": {
							guard: "eventIsDone",
							target: "#gitRepository.queue.idle",
						},
					},
				},
			},
			on: {
				"git.*": {
					description: "If the event is a command request, add it to the queue",
					guard: "eventIsRequest",
					actions: "addToQueue",
				},
			},
		},

		commands: {
			description: "Command runner",
			initial: "waitingForCommands",
			states: {
				waitingForCommands: {
					description: "The queue is empty, waiting for a command to be requested",
					on: {
						"git.commit.processing": {
							target: "#gitRepository.commands.committing",
						},
						"git.diff.processing": {
							target: "#gitRepository.commands.diffing",
						},
						"git.init.processing": {
							target: "#gitRepository.commands.initializing",
						},
						"git.log.processing": {
							target: "#gitRepository.commands.logging",
						},
						"git.fetch.processing": {
							target: "#gitRepository.commands.fetching",
						},
						"git.push.processing": {
							target: "#gitRepository.commands.pushing",
						},
						"git.pull.processing": {
							target: "#gitRepository.commands.pulling",
						},
						"git.remote.add.processing": {
							target: "#gitRepository.commands.addingRemote",
						},
						"git.remote.list.processing": {
							target: "#gitRepository.commands.listingRemotes",
						},
						"git.show.processing": {
							target: "#gitRepository.commands.showing",
						},
						"git.stage.processing": {
							target: "#gitRepository.commands.staging",
						},
						"git.branch.list.processing": {
							target: "#gitRepository.commands.listingBranches",
						},
						"git.branch.track.processing": {
							target: "#gitRepository.commands.addingUpstreamBranch",
						},
						"git.branch.new.processing": {
							target: "#gitRepository.commands.creatingBranch",
						},
						"git.branch.switch.processing": {
							target: "#gitRepository.commands.switchingBranch",
						},
						"git.branch.merge.processing": {
							target: "#gitRepository.commands.mergingBranch",
						},
						"git.branch.analyze.processing": {
							target: "#gitRepository.commands.analyzingMerge",
						},
						"git.branch.delete.processing": {
							target: "#gitRepository.commands.deletingBranch",
						},
						"git.branch.aheadbehind.processing": {
							target: "#gitRepository.commands.aheadingbehindingBranch",
						},
						"git.status.processing": {
							target: "#gitRepository.commands.stating",
						},
					},
				},

				addingRemote: {
					description: "git remote add",
					invoke: {
						src: "gitRemoteAdd",
						input: ({ context, event }) => ({
							path: context.path,
							remote: (event as GitRemoteAddEvent).payload.remote,
							url: (event as GitRemoteAddEvent).payload.url,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				creatingBranch: {
					description: "git branch create",
					invoke: {
						src: "gitBranchNew",
						input: ({ event }) => ({
							path: (event as GitBranchCreateEvent).payload.path,
							name: (event as GitBranchCreateEvent).payload.name,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: "updateBranches",
				},

				committing: {
					description: "git commit",
					invoke: {
						src: "gitCommit",
						input: ({ context, event }) => ({
							path: context.path,
							message: (event as GitCommitEvent).payload.message,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: ["updateStatus", "updateDiff"],
				},

				diffing: {
					description: "git diff",
					invoke: {
						src: "gitDiff",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				fetching: {
					description: "git fetch",
					invoke: {
						src: "gitFetch",
						input: ({ context }) => ({
							path: context.path,
							remote: "moment",
							branch: context.status!.branch,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				initializing: {
					description: "git init",
					invoke: {
						src: "gitInit",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: "updateStatus",
				},

				listingRemotes: {
					description: "git remote -vv",
					invoke: {
						src: "gitRemoteList",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: [
								assign(({ event }) => ({
									remotes: event.output,
								})),
								"processingEventDone",
							],
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				logging: {
					description: "git log",
					invoke: {
						src: "gitLog",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: [
								assign(({ event }) => ({
									history: event.output,
								})),
								"processingEventDone",
							],
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				listingBranches: {
					description: "git branch",
					invoke: {
						src: "gitBranchList",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: [
								assign(({ event }) => ({
									branches: event.output,
								})),
								"processingEventDone",
							],
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				addingUpstreamBranch: {
					description: "git branch --set-upstream",
					invoke: {
						src: "gitBranchTrack",
						input: ({ context, event }) => ({
							path: context.path,
							remote: (event as GitBranchTrackEvent).payload.remote,
							localBranch: (event as GitBranchTrackEvent).payload.localBranch,
							upstreamBranch: (event as GitBranchTrackEvent).payload.upstreamBranch,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				pulling: {
					description: "git pull",
					invoke: {
						src: "gitPull",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: "updateStatus",
				},

				pushing: {
					description: "git push",
					invoke: {
						src: "gitPush",
						input: ({ context }) => ({
							path: context.path,
							remote:
								(context.commandQueue[0] as GitPushEvent).payload.remote ||
								context.remotes[0]?.name ||
								GIT_PUBLISHING_REMOTE_NAME,
							branch:
								(context.commandQueue[0] as GitPushEvent).payload.branch ||
								context.status!.branch ||
								GIT_PUBLISHING_BRANCH_NAME,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: "updateStatus",
				},

				showing: {
					description: "git show",
					invoke: {
						src: "gitShow",
						input: ({ context, event }) => ({
							path: context.path,
							commitHash: (event as GitShowEvent).payload.commitHash,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				staging: {
					description: "git stage",
					invoke: {
						src: "gitStage",
						input: ({ context, event }) => ({
							path: context.path,
							files: (event as GitStageEvent).payload.files,
							operation: (event as GitStageEvent).payload.operation,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: ["updateStatus", "updateDiff"],
				},

				stating: {
					description: "git status",
					invoke: {
						src: "gitStatus",
						input: ({ context }) => ({
							path: context.path,
						}),
						onDone: {
							actions: [
								assign(({ event }) => ({
									status: event.output,
								})),
								"processingEventDone",
							],
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},

				switchingBranch: {
					description: "git checkout branch",
					invoke: {
						src: "gitBranchSwitch",
						input: ({ event }) => ({
							path: (event as GitBranchSwitchEvent).payload.path,
							target: (event as GitBranchSwitchEvent).payload.target,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: [
						"updateBranches",
						"updateStatus",
						"updateDiff",
						sendParent({ type: "git.branchSwitched" }),
					],
				},

				mergingBranch: {
					description: "git merge --into source target",
					invoke: {
						src: "gitBranchMerge",
						input: ({ context, event }) => ({
							path: context.path,
							source: (event as GitBranchMergeEvent).payload.source,
							target: (event as GitBranchMergeEvent).payload.target,
							deleteSource: (event as GitBranchMergeEvent).payload.deleteSource,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: [
						"updateBranches",
						"updateStatus",
						"updateDiff",
						sendParent({ type: "git.branchSwitched" }),
					],
				},

				deletingBranch: {
					description: "git branch -d name",
					invoke: {
						src: "gitBranchDelete",
						input: ({ context, event }) => ({
							path: context.path,
							name: (event as GitBranchDeleteEvent).payload.name,
							force: (event as GitBranchDeleteEvent).payload.force,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: ["updateBranches"],
				},

				aheadingbehindingBranch: {
					description: "How many commits ahead/behind?",
					invoke: {
						src: "gitBranchAheadBehind",
						input: ({ context, event }) => ({
							path: context.path,
							source: (event as GitBranchAheadBehindEvent).payload.source,
							target: (event as GitBranchAheadBehindEvent).payload.target,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
					exit: ["updateBranches"],
				},

				analyzingMerge: {
					description: "can we merge? how complex would it be?",
					invoke: {
						src: "gitBranchAnalyze",
						input: ({ context, event }) => ({
							path: context.path,
							source: (event as GitBranchAnalyzeEvent).payload.source,
							target: (event as GitBranchAnalyzeEvent).payload.target,
						}),
						onDone: {
							actions: "processingEventDone",
							target: "#gitRepository.commands.waitingForCommands",
						},
						onError: {
							actions: "processingEventError",
							target: "#gitRepository.commands.waitingForCommands",
						},
					},
				},
			},
		},
	},
});
